// ===================================
// ADMIN ANALYTICS PAGE - STAGE 7
// src/pages/AdminAnalytics.js
// Platform analytics and metrics
// ===================================

import React, { useState, useEffect } from 'react';
import { getAnalytics, getAdminStats } from '../services/adminApi';
import './AdminAnalytics.css';

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('30');

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, statsRes] = await Promise.all([
        getAnalytics({ period }),
        getAdminStats()
      ]);
      setAnalytics(analyticsRes);
      setStats(statsRes);
    } catch (err) {
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const getMaxValue = (data, key) => {
    if (!data || data.length === 0) return 1;
    return Math.max(...data.map(d => d[key] || 0), 1);
  };

  if (loading) {
    return (
      <div className="admin-analytics-page">
        <div className="loading-container"><div className="spinner"></div><p>Loading analytics...</p></div>
      </div>
    );
  }

  return (
    <div className="admin-analytics-page">
      <div className="page-header">
        <div className="header-content">
          <h1>📈 Analytics Dashboard</h1>
          <p>Platform performance and insights</p>
        </div>
        <div className="period-selector">
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Overview Stats */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <span className="stat-value">{stats.users?.total_users || 0}</span>
              <span className="stat-label">Total Users</span>
              <span className="stat-change positive">+{stats.users?.new_users_week || 0} this week</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <span className="stat-value">{stats.sessions?.total_sessions || 0}</span>
              <span className="stat-label">Total Sessions</span>
              <span className="stat-secondary">{stats.sessions?.completed_sessions || 0} completed</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-content">
              <span className="stat-value">{stats.sessions?.avg_score || '-'}%</span>
              <span className="stat-label">Average Score</span>
            </div>
          </div>
          <div className="stat-card highlight">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <span className="stat-value">${((stats.revenue?.total_revenue || 0) / 100).toFixed(0)}</span>
              <span className="stat-label">Total Revenue</span>
              <span className="stat-change positive">+${((stats.revenue?.revenue_week || 0) / 100).toFixed(0)} this week</span>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="charts-grid">
        {/* Sessions Over Time */}
        <div className="chart-card">
          <h3>Sessions Over Time</h3>
          <div className="chart-container">
            {analytics?.sessionsOverTime?.length > 0 ? (
              <div className="bar-chart">
                {analytics.sessionsOverTime.slice(-14).map((day, i) => (
                  <div key={i} className="bar-group">
                    <div className="bar-stack">
                      <div className="bar completed" style={{ height: `${(day.completed / getMaxValue(analytics.sessionsOverTime, 'total')) * 100}%` }} title={`Completed: ${day.completed}`}></div>
                      <div className="bar pending" style={{ height: `${((day.total - day.completed) / getMaxValue(analytics.sessionsOverTime, 'total')) * 100}%` }} title={`Other: ${day.total - day.completed}`}></div>
                    </div>
                    <span className="bar-label">{new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>
                ))}
              </div>
            ) : <div className="no-data">No data available</div>}
          </div>
          <div className="chart-legend">
            <span className="legend-item"><span className="dot completed"></span> Completed</span>
            <span className="legend-item"><span className="dot pending"></span> Other</span>
          </div>
        </div>

        {/* Revenue Over Time */}
        <div className="chart-card">
          <h3>Revenue Over Time</h3>
          <div className="chart-container">
            {analytics?.revenueOverTime?.length > 0 ? (
              <div className="bar-chart">
                {analytics.revenueOverTime.slice(-14).map((day, i) => (
                  <div key={i} className="bar-group">
                    <div className="bar-stack">
                      <div className="bar revenue" style={{ height: `${(day.revenue / getMaxValue(analytics.revenueOverTime, 'revenue')) * 100}%` }} title={`$${(day.revenue / 100).toFixed(2)}`}></div>
                    </div>
                    <span className="bar-label">{new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>
                ))}
              </div>
            ) : <div className="no-data">No data available</div>}
          </div>
        </div>

        {/* Interview Types */}
        <div className="chart-card">
          <h3>Interview Types</h3>
          <div className="type-breakdown">
            {stats?.typeBreakdown?.length > 0 ? (
              stats.typeBreakdown.map((type, i) => (
                <div key={i} className="type-item">
                  <div className="type-icon">{type.interview_type === 'text' ? '💬' : type.interview_type === 'voice' ? '🎙️' : '📞'}</div>
                  <div className="type-info">
                    <span className="type-name capitalize">{type.interview_type}</span>
                    <span className="type-count">{type.count} sessions</span>
                  </div>
                  <div className="type-score">{type.avg_score ? `${type.avg_score}% avg` : '-'}</div>
                </div>
              ))
            ) : <div className="no-data">No data</div>}
          </div>
        </div>

        {/* Score Distribution */}
        <div className="chart-card">
          <h3>Score Distribution</h3>
          <div className="distribution-chart">
            {analytics?.scoreDistribution?.length > 0 ? (
              analytics.scoreDistribution.map((range, i) => (
                <div key={i} className="distribution-row">
                  <span className="distribution-label">{range.range}</span>
                  <div className="distribution-bar">
                    <div className="distribution-fill" style={{ width: `${(range.count / Math.max(...analytics.scoreDistribution.map(r => r.count))) * 100}%` }}></div>
                  </div>
                  <span className="distribution-count">{range.count}</span>
                </div>
              ))
            ) : <div className="no-data">No data</div>}
          </div>
        </div>

        {/* Top Users */}
        <div className="chart-card wide">
          <h3>Top Users by Sessions</h3>
          <div className="top-users-table">
            {analytics?.topUsers?.length > 0 ? (
              <table>
                <thead><tr><th>Rank</th><th>User</th><th>Sessions</th><th>Avg Score</th></tr></thead>
                <tbody>
                  {analytics.topUsers.slice(0, 5).map((user, i) => (
                    <tr key={user.id}>
                      <td><span className="rank-badge">#{i + 1}</span></td>
                      <td><div className="user-cell"><span className="user-name">{user.name || user.username || 'User'}</span><span className="user-email">{user.email}</span></div></td>
                      <td><strong>{user.session_count}</strong></td>
                      <td>{user.avg_score ? <span className="score-badge">{user.avg_score}%</span> : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="no-data">No users yet</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
